import * as React from 'react';
import {Mutation, Query} from 'react-apollo';
import {Helmet} from 'react-helmet';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import * as ramda from 'ramda';
import {compose} from 'redux';

import {headTitle} from 'shells/document/head-title';

import type {
    StagesetAndStages as StagesetAndStagesQuery,
    StagesetAndStagesVariables as StagesetAndStagesQueryVariables,
    MoveStage as MoveStageMutation,
    MoveStageVariables as MoveStageMutationVariables,
    SalesAutomationStageset as SalesAutomationStagesetFragment,
} from 'nutshell-graphql-types';

import MOVE_STAGE from '../graphql/mutations/move-stage.graphql';
import GET_SLACK_MESSAGES_FOR_STAGE from '../graphql/queries/get-slack-messages-for-stage.graphql';
import GET_STAGE_GOALS_FOR_STAGE from '../graphql/queries/get-stage-goals-for-stage.graphql';
import GET_STAGESET_AND_STAGES from '../graphql/queries/get-stageset-and-stages.graphql';
import GET_STEP_TEMPLATES_FOR_STAGE from '../graphql/queries/get-step-templates-for-stage.graphql';
import GET_TEXT_AUTOMATIONS_FOR_STAGE from '../graphql/queries/get-text-automations-for-stage.graphql';
import GET_USER_ASSIGNMENTS_FOR_STAGE from '../graphql/queries/get-user-assignments-for-stage.graphql';
import type {AvailableIntegrations} from '../types';
import {addNewStage} from '../ui-actions';

import {StageHeaders} from './header/stage-headers';
import {SalesAutomationOpenLeadsBoard} from './sales-automation-open-leads-board';
import {SalesAutomationOpenLeadsBoardEmpty} from './sales-automation-open-leads-board-empty';
import {SalesAutomationAppShell} from './shell/sales-automation-app-shell';

type OwnProps = Record<any, any>;

type RoutedProps = ContextRouter & OwnProps;

type DispatchProps = {
    onCreateNewStage: () => void;
};

type ConnectedProps = RoutedProps & DispatchProps;

type GraphQLProps = {
    stageset: SalesAutomationStagesetFragment | null | undefined;
    availableIntegrations: AvailableIntegrations;
    onStageMoved: (arg1: {stageId: string; position: number}) => Promise<any>;
    onRefetchStageset: () => any;
};

type Props = ConnectedProps & GraphQLProps;

type State = {
    isStageMoving: boolean;
};

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onCreateNewStage: () => {
            dispatch(addNewStage());
        },
    };
}

/*
This is how we update our stageset board after its been updated. When items are
updated, we delete the old items and create all new ones with new ids, so we
need to refetch the whole board. This contains a list of all queries to be run
with any update.
 */
export function refetchSalesAutomationOpenLeadQueries(variables: {
    stageId: string;
}): string | any[] {
    return [
        {query: GET_USER_ASSIGNMENTS_FOR_STAGE, variables},
        {query: GET_STAGE_GOALS_FOR_STAGE, variables},
        {query: GET_STEP_TEMPLATES_FOR_STAGE, variables},
        {query: GET_SLACK_MESSAGES_FOR_STAGE, variables},
        {query: GET_TEXT_AUTOMATIONS_FOR_STAGE, variables},
        'StagesetAndStages',
        'EmailsForStagesetQuery',
    ];
}

/*
 * Renders the stages board within the SalesAutomationAppShell, which
 * provides the navigation and editing sidebars.
 */
export class SalesAutomationOpenLeadsComponent extends React.PureComponent<Props, State> {
    // @ts-expect-error
    scrollContainerRef: HTMLElement;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isStageMoving: false,
        };
    }

    render() {
        const {stageset} = this.props;
        if (!stageset) return null;

        return (
            <React.Fragment>
                <Helmet>
                    <title>{headTitle(stageset.name)}</title>
                </Helmet>
                <SalesAutomationAppShell
                    stageset={stageset}
                    shouldObscureBoard={this.state.isStageMoving}
                    headers={
                        stageset.stages.length ? (
                            <StageHeaders
                                stages={stageset.stages}
                                onStageDragStart={this.handleStageDragStart}
                                onStageDragEnd={this.handleStageDragEnd}
                            />
                        ) : null
                    }
                    board={
                        stageset.stages.length ? (
                            <SalesAutomationOpenLeadsBoard
                                stageset={stageset}
                                availableIntegrations={this.props.availableIntegrations}
                                isStageMoving={this.state.isStageMoving}
                                onRefetchStageset={this.props.onRefetchStageset}
                            />
                        ) : (
                            <SalesAutomationOpenLeadsBoardEmpty
                                onCreateStage={this.props.onCreateNewStage}
                            />
                        )
                    }
                />
            </React.Fragment>
        );
    }

    handleStageDragStart = () => {
        this.setState({isStageMoving: true});
    };

    handleStageDragEnd = (result?: {stageId: string; position: number}) => {
        if (result) this.props.onStageMoved(result);

        this.setState({isStageMoving: false});
    };
}

// @ts-expect-error
const connector = connect<ConnectedProps, RoutedProps, _, _, _, _>(null, mapDispatchToProps);

class StagesQuery extends Query<StagesetAndStagesQuery, StagesetAndStagesQueryVariables> {}
class MoveMutation extends Mutation<MoveStageMutation, MoveStageMutationVariables> {}

const SalesAutomationOpenLeadsGraphQL = (props: ConnectedProps) => (
    <StagesQuery
        query={GET_STAGESET_AND_STAGES}
        variables={{stagesetId: props.match.params.stagesetId || '1-stagesets'}}
    >
        {({data, refetch}) => (
            <MoveMutation mutation={MOVE_STAGE}>
                {(moveStage) => {
                    const {
                        stageset,
                        hasSlackIntegration,
                        hasMailChimpIntegration,
                        hasConstantContactIntegration,
                    } = data || {};

                    return (
                        <SalesAutomationOpenLeadsComponent
                            onCreateNewStage={props.onCreateNewStage}
                            onRefetchStageset={refetch}
                            history={props.history}
                            location={props.location}
                            match={props.match}
                            stageset={stageset}
                            availableIntegrations={{
                                hasMailChimpIntegration,
                                hasSlackIntegration,
                                hasConstantContactIntegration,
                            }}
                            // @ts-expect-error
                            onStageMoved={({stageId, position}) => {
                                if (!stageset) {
                                    return Promise.reject(
                                        'No stageset loaded, cannot move stages.'
                                    );
                                }
                                const stages = stageset.stages;
                                const movedStage = stages.find((stage) => stage.id === stageId);

                                if (!movedStage) {
                                    return Promise.reject('Cannot find stage to move');
                                }

                                const reorderStages = compose(
                                    ramda.insert(position - 1, movedStage),
                                    ramda.without([movedStage])
                                );
                                const reorderedStages = reorderStages(stages);

                                return moveStage({
                                    variables: {
                                        stage: {
                                            id: stageId,
                                            position, // Position is 1 indexed instead of 0
                                        },
                                    },
                                    optimisticResponse: {
                                        // @ts-expect-error
                                        __typename: 'Mutation',
                                        stageUpdate: {
                                            __typename: 'StageUpdatePayload',
                                            stageset: {
                                                ...stageset,
                                                stages: reorderedStages,
                                            },
                                        },
                                    },
                                });
                            }}
                        />
                    );
                }}
            </MoveMutation>
        )}
    </StagesQuery>
);

// @ts-expect-error
export const SalesAutomationOpenLeads = withRouter<OwnProps>(
    connector(SalesAutomationOpenLeadsGraphQL)
);
